import { NgIf } from '@angular/common';
import { Directive, HostListener, inject, isDevMode as isAngularDevMode, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { defaults, get } from 'lodash';

import { SessionStorage } from '@lockton-apps/shared/data-access/storage';
import { QUOTE_PERMISSIONS } from '@lockton-apps/shared/interfaces';

export interface DevConfig {
  active: boolean;
  proposalForm: DevConfigProposalForm;
  quote: DevConfigQuote;
  client: DevConfigClient;
}

export interface DevConfigProposalForm {
  runLocally: boolean;
  mockData: boolean;
}

export interface DevConfigQuote {
  permissions: string[];
}

export interface DevConfigClient {
  fullMock: boolean;
}

const storeKey = 'dev';
const initialState: DevConfig = {
  active: false,
  proposalForm: {
    runLocally: false,
    mockData: false,
  },
  quote: {
    permissions: [QUOTE_PERMISSIONS.canUpdateProposal],
  },
  client: {
    fullMock: false,
  },
};

export function isDevMode(): boolean {
  return getDevConfig('active');
}

export function toggleDevMode(): void {
  const config: DevConfig = getConfig() || initialState;

  config.active = !config.active;

  setConfig(config);

  window.location.reload();
}

export function getDevConfig(path?: string): any {
  const config: Partial<DevConfig> = getConfig();

  if (!config?.active) {
    return null;
  }

  return path ? get(config, path) : config;
}

export function setDevConfig(config: Partial<DevConfig>): void {
  const currentConfig: DevConfig = getDevConfig();

  setConfig(defaults(config, currentConfig));

  window.location.reload();
}

function getConfig() {
  return JSON.parse(SessionStorage.get(storeKey) as string);
}

function setConfig(config: DevConfig) {
  SessionStorage.set(storeKey, JSON.stringify(config));
}

@Directive({
  selector: '[laIfDev]',
  standalone: true,
  hostDirectives: [NgIf],
})
export class IfDevDirective {
  private ngIf = inject(NgIf, { host: true });

  isDev = getDevConfig('active');

  constructor() {
    this.ngIf.ngIf = this.isDev;
  }
}

@Directive({
  selector: '[laDevManager]',
  standalone: true,
})
export class DevModeManagerDirective {
  private isCtrlDown!: boolean;
  private isShiftDown!: boolean;

  @HostListener('document:keyup', ['$event'])
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!isAngularDevMode()) {
      return;
    }
    this.isCtrlDown = event.ctrlKey || event.metaKey;
    this.isShiftDown = event.shiftKey || event.shiftKey;

    switch (event.code) {
      case 'KeyD':
        if (!this.isCtrlDown || !this.isShiftDown) {
          return;
        }

        toggleDevMode();
        event.preventDefault();
    }

    this.isCtrlDown = false;
    this.isShiftDown = false;
  }
}

export function provideInitDevMode(isProduction: boolean) {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
        const initializerFn = (() => {
        return () => {
          if (isProduction) {
            window.sessionStorage.removeItem(storeKey);
          }
        };
      })();
        return initializerFn();
      }),
  ]);
}
